import { useReducer, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { change } from 'redux-form'

import {
  getRegionalDiscount,
  getRegionalDiscountLoaded,
  fetchRegionalDiscount,
} from 'ducks/regionalDiscount'
import { fetchProratedCharge, fetchCoupon } from 'ducks/trialWarning'

const { INITIALIZING, HIDDEN, EDITING, VALIDATING, APPLIED, ERROR } =
  Object.freeze({
    INITIALIZING: 'initializing',
    HIDDEN: 'hidden',
    EDITING: 'editing',
    VALIDATING: 'validating',
    APPLIED: 'applied',
    ERROR: 'error',
  })

const initialState = {
  status: INITIALIZING,
  coupon: null,
  regionalCouponData: null,
  isRegionalDiscount: false,
  errorMessage: null,
  visible: false,
}

function couponReducer(state, action) {
  switch (action.type) {
    case 'INITIALIZE_WITH_REGIONAL':
      return {
        ...state,
        status: APPLIED,
        coupon: action.payload.coupon,
        regionalCouponData: action.payload.coupon,
        isRegionalDiscount: true,
        visible: true,
        errorMessage: null,
      }

    case 'INITIALIZE_WITHOUT_COUPON':
      return {
        ...state,
        status: HIDDEN,
        coupon: null,
        isRegionalDiscount: false,
        visible: false,
        errorMessage: null,
      }

    case 'SHOW_INPUT':
      return {
        ...state,
        status: EDITING,
        errorMessage: null,
        visible: true,
      }

    case 'START_VALIDATION':
      return {
        ...state,
        status: VALIDATING,
        errorMessage: null,
      }

    case 'APPLY_SUCCESS':
      return {
        ...state,
        status: APPLIED,
        coupon: action.payload.coupon,
        isRegionalDiscount: false,
        errorMessage: null,
      }

    case 'APPLY_ERROR':
      return {
        ...state,
        status: ERROR,
        coupon: null,
        isRegionalDiscount: false,
        errorMessage: action.payload.error,
      }

    case 'APPLY_REGIONAL_FALLBACK':
      return {
        ...state,
        status: APPLIED,
        coupon: state.regionalCouponData,
        isRegionalDiscount: true,
        visible: true,
        errorMessage: null,
      }

    case 'CLEAR_COUPON':
      return {
        ...state,
        status: HIDDEN,
        coupon: null,
        isRegionalDiscount: false,
        visible: false,
        errorMessage: null,
      }

    default:
      throw new Error(`Unhandled action type: ${action.type}`)
  }
}

const useCouponManagement = ({
  organizationId,
  selectedPlanValue,
  annual,
  additionalPublishedApps,
  additionalTeamMembers,
}) => {
  const reduxDispatch = useDispatch()
  const [state, dispatch] = useReducer(couponReducer, initialState)

  const regionalDiscount = useSelector(getRegionalDiscount)
  const regionalDiscountLoaded = useSelector(getRegionalDiscountLoaded)
  const couponCode = useSelector(
    state => state.form?.trialModalSignup?.values?.coupon
  )

  useEffect(() => {
    if (!regionalDiscountLoaded) {
      reduxDispatch(fetchRegionalDiscount())
    }
  }, [regionalDiscountLoaded, reduxDispatch])

  useEffect(() => {
    if (regionalDiscountLoaded && state.status === INITIALIZING) {
      let initialCouponCode = null

      const shouldApplyRegionalDiscount =
        regionalDiscount?.couponApplied && regionalDiscount?.couponId

      if (shouldApplyRegionalDiscount) {
        const regionalCoupon = {
          id: regionalDiscount.couponId,
          ...regionalDiscount,
        }

        dispatch({
          type: 'INITIALIZE_WITH_REGIONAL',
          payload: { coupon: regionalCoupon },
        })

        reduxDispatch(
          change('trialModalSignup', 'coupon', regionalDiscount.couponId)
        )
        initialCouponCode = regionalDiscount.couponId
      } else {
        dispatch({ type: 'INITIALIZE_WITHOUT_COUPON' })
      }

      reduxDispatch(
        fetchProratedCharge(
          organizationId,
          selectedPlanValue,
          annual,
          initialCouponCode,
          additionalPublishedApps,
          additionalTeamMembers
        )
      )
    }
  }, [
    regionalDiscountLoaded,
    regionalDiscount?.couponApplied,
    regionalDiscount?.couponId,
    state.status,
    organizationId,
    selectedPlanValue,
    annual,
    additionalPublishedApps,
    additionalTeamMembers,
    reduxDispatch,
  ])

  const showCoupon = () => {
    dispatch({ type: 'SHOW_INPUT' })
  }

  const getCoupon = async () => {
    dispatch({ type: 'START_VALIDATION' })

    if (!couponCode || couponCode.trim() === '') {
      if (state.regionalCouponData) {
        dispatch({ type: 'APPLY_REGIONAL_FALLBACK' })
        reduxDispatch(
          change('trialModalSignup', 'coupon', state.regionalCouponData.id)
        )
        reduxDispatch(
          fetchProratedCharge(
            organizationId,
            selectedPlanValue,
            annual,
            state.regionalCouponData.id,
            additionalPublishedApps,
            additionalTeamMembers
          )
        )
      } else {
        dispatch({ type: 'CLEAR_COUPON' })
        reduxDispatch(change('trialModalSignup', 'coupon', null))
        reduxDispatch(
          fetchProratedCharge(
            organizationId,
            selectedPlanValue,
            annual,
            null,
            additionalPublishedApps,
            additionalTeamMembers
          )
        )
      }

      return
    }

    try {
      const data = await fetchCoupon(couponCode)

      if (!data.error) {
        dispatch({
          type: 'APPLY_SUCCESS',
          payload: { coupon: data },
        })

        reduxDispatch(
          fetchProratedCharge(
            organizationId,
            selectedPlanValue,
            annual,
            couponCode,
            additionalPublishedApps,
            additionalTeamMembers
          )
        )
      } else {
        dispatch({
          type: 'APPLY_ERROR',
          payload: { error: data.error || 'An unknown error occurred' },
        })

        reduxDispatch(
          fetchProratedCharge(
            organizationId,
            selectedPlanValue,
            annual,
            null,
            additionalPublishedApps,
            additionalTeamMembers
          )
        )
      }
    } catch (error) {
      dispatch({
        type: 'APPLY_ERROR',
        payload: { error: 'An unknown error occurred' },
      })
    }
  }

  return {
    coupon: state.coupon,
    couponError: state.errorMessage,
    couponVisible: state.visible,
    couponSuccess: state.status === APPLIED,
    isRegionalDiscount: state.isRegionalDiscount,
    showCoupon,
    getCoupon,
  }
}

export default useCouponManagement
